import { useEffect, useMemo, useState } from 'react';
import { Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useIsPwa } from '@/hooks/useIsPwa';

const DISMISS_KEY = 'velko-pwa-install-dismissed-v1';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export function PwaInstallBanner() {
  const isPwa = useIsPwa();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    setDismissed(localStorage.getItem(DISMISS_KEY) === '1');

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };

    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      localStorage.removeItem(DISMISS_KEY);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const shouldRender = useMemo(() => {
    return !dismissed && !installed && !isPwa && deferredPrompt !== null;
  }, [deferredPrompt, dismissed, installed, isPwa]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;

    if (choice.outcome === 'accepted') {
      setInstalled(true);
      localStorage.removeItem(DISMISS_KEY);
    }

    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <div className="fixed inset-x-3 bottom-[calc(var(--bottom-tab-height)+var(--bottom-tab-safe-area)+0.75rem)] z-30 lg:hidden">
      <Card className="bg-white/95 dark:bg-card/95 backdrop-blur">
        <CardContent className="flex items-center gap-3 p-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-lime-100 text-lime-700">
            <Smartphone className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-charcoal-900">Installer Velko POS</p>
            <p className="text-xs text-charcoal-500">
              Ajoutez l'application a votre ecran d'accueil.
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={handleDismiss}>
              Plus tard
            </Button>
            <Button type="button" size="sm" onClick={() => void handleInstall()}>
              <Download className="h-3.5 w-3.5" />
              Installer
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
